import React, { useState } from "react";
import { Typography, Box, Grid, SelectChangeEvent } from "@mui/material";
import { useTranslation } from "react-i18next";
import colors from "../../Constants/colors";
import { ExpertFormState } from "../../Models/expert";
import ExpertName from "./ExpertName";
import SkillsAndAbilities from "./SkillsAndAbilities";
import RolesAndResponsibilities from "./RolesAndResponsibilities";
import KnowledgeBaseAndCurrentTrends from "./KnowledgeBaseAndCurrentTrends";
import ModelBehavior from "./ModelBehavior";
import { useCreateExpert } from "../../Query/useCreateExpert";
import { useNavigate } from "react-router-dom";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ComoButton } from "../Common/ComoButton";

const initialFormState: ExpertFormState = {
  expertName: "",
  professionalBackground: "",
  keyExpertise: "",
  trainingAndCertifications: "",
  processMastery: "",
  primaryResponsibilities: "",
  problemSolvingAbilities: "",
  knowledgeDepth: "",
  knowledgebase: [],
  trends: [],
  modelBehavior: "consistent",
};

const ExpertForm: React.FC = () => {
  const { t } = useTranslation("expertForm");
  const [formState, setFormState] = useState<ExpertFormState>(initialFormState);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const { mutate, isPending: isMutating, isSuccess } = useCreateExpert();
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleLinkChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    section: "knowledgebase" | "trends",
    index: number,
  ) => {
    const { value } = e.target;
    const updatedLinks = [...formState[section]];
    updatedLinks[index] = value;
    setFormState((prevState) => ({
      ...prevState,
      [section]: updatedLinks,
    }));
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`${section}-${index}`]: "",
    }));
  };

  const handleAddLink = (section: "knowledgebase" | "trends") => {
    setFormState((prevState) => ({
      ...prevState,
      [section]: [...prevState[section], ""],
    }));
  };

  const handleRemoveLink = (
    section: "knowledgebase" | "trends",
    index: number,
  ) => {
    const updatedLinks = [...formState[section]];
    updatedLinks.splice(index, 1);
    setFormState((prevState) => ({
      ...prevState,
      [section]: updatedLinks,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};
    Object.keys(formState).forEach((key) => {
      if (!formState[key as keyof ExpertFormState]) {
        newErrors[key] = t("expertForm.requiredField");
      }
    });
    if (Object.keys(newErrors).length) {
      setValidationErrors(newErrors);
      return;
    }
    mutate(formState, {
      onSuccess: () => {
        setFormState(initialFormState);
      },
      onError: (err) => {
        if (err.response?.status === 403) {
          navigate("/home");
        }
      },
    });
  };

  if (isMutating) {
    return (
      <Loading
        backgroundColor={colors.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isSuccess) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 64px)",
          backgroundColor: colors.gray50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            maxWidth: "600px",
            mx: "auto",
            px: 3,
            py: 4,
          }}
        >
          <Typography
            variant="h5"
            textAlign="center"
            sx={{ color: workspace.primary_dark, fontWeight: 600 }}
          >
            {t("expertForm.submissionSuccessMessage")}
          </Typography>
          <Box mt={2} textAlign="center">
            <ComoButton onClick={() => navigate("/home")}>
              {t("expertForm.backToHomepage")}
            </ComoButton>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: colors.gray50 }}>
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          sx={{ color: workspace.primary_dark, fontWeight: 600 }}
        >
          {t("expertForm.title")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 4 }}>
          <Grid container justifyContent="center">
            <ExpertName
              formState={formState}
              handleChange={handleChange}
              validationErrors={validationErrors}
            />

            <SkillsAndAbilities
              formState={formState}
              handleChange={handleChange}
              validationErrors={validationErrors}
            />

            <RolesAndResponsibilities
              formState={formState}
              handleChange={handleChange}
              validationErrors={validationErrors}
            />

            <KnowledgeBaseAndCurrentTrends
              formState={formState}
              handleLinkChange={handleLinkChange}
              handleAddLink={handleAddLink}
              handleRemoveLink={handleRemoveLink}
            />

            <ModelBehavior
              value={formState.modelBehavior}
              handleChange={handleSelectChange}
              error={!!validationErrors.modelBehavior}
              helperText={validationErrors.modelBehavior}
            />
          </Grid>

          <Grid item xs={12}>
            <Box mt={2} textAlign="center">
              <ComoButton type="submit">{t("expertForm.submit")}</ComoButton>
            </Box>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpertForm;
